package pl.zar.tikthrow.util

import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.viewmodel.PublishingPlan
import pl.zar.tikthrow.viewmodel.QueuedPost
import pl.zar.tikthrow.viewmodel.ScheduledPost

data class PlatformPublishResult(
    val success: Boolean,
    val message: String,
    val serverId: String? = null,
)

data class ServerQueueItem(
    val id: String,
    val position: Int,
    val type: String,
    val title: String,
    val caption: String,
    val platforms: List<String>,
    val youtubeAccountId: String?,
    val tiktokAccountId: String?,
    val status: String,
    val errorMessage: String?,
)

const val PUBLISH_CONNECT_TIMEOUT_MILLIS = 30_000
const val PUBLISH_READ_TIMEOUT_MILLIS = 30 * 60 * 1000

fun userFacingNetworkError(
    action: String,
    throwable: Throwable,
): String {
    val type = throwable::class.simpleName.orEmpty()
    val message = throwable.message.orEmpty()
    val combined = "$type $message"

    val reason = when {
        combined.contains("SocketTimeout", ignoreCase = true) ||
            combined.contains("TimeoutCancellation", ignoreCase = true) ||
            combined.contains("timed out", ignoreCase = true) ->
            "przekroczono limit czasu oczekiwania na serwer albo zewnetrzne API. Sprobuj ponownie; przy duzym pliku publikacja moze trwac kilkanascie minut"
        combined.contains("ConnectException", ignoreCase = true) ||
            combined.contains("Connection refused", ignoreCase = true) ->
            "nie udalo sie polaczyc z serwerem"
        combined.contains("UnknownHost", ignoreCase = true) ->
            "nie mozna odnalezc adresu serwera"
        combined.contains("SSL", ignoreCase = true) ||
            combined.contains("CertPath", ignoreCase = true) ->
            "wystapil problem z bezpiecznym polaczeniem"
        combined.contains("Cancellation", ignoreCase = true) ->
            "operacja zostala przerwana"
        message.isNotBlank() -> message
        type.isNotBlank() -> type
        else -> "nieznany blad"
    }

    return "$action nie powiodlo sie: $reason"
}

expect suspend fun publishScheduledPost(post: ScheduledPost): PlatformPublishResult

expect suspend fun scheduleScheduledPostOnServer(post: ScheduledPost): PlatformPublishResult

expect suspend fun scheduleTikTokPostOnServer(post: ScheduledPost): PlatformPublishResult

expect suspend fun savePlanToServer(plan: PublishingPlan): Boolean

expect suspend fun loadPlanFromServer(): PublishingPlan?

expect suspend fun addPostToServerQueue(
    post: QueuedPost,
    media: List<MediaItem>,
): String?

/** Update the title/description a queued post will publish with. */
expect suspend fun updateQueuedPostOnServer(serverId: String, title: String, caption: String): Boolean

expect suspend fun removePostFromServerQueue(serverId: String): Boolean

expect suspend fun loadServerQueue(): List<ServerQueueItem>?

expect suspend fun reorderServerQueue(serverIds: List<String>): Boolean

/** Returns null on success, or an error message if the server rejected the retry. */
expect suspend fun retryPostOnServer(serverId: String): String?
